import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  ARRAY_CONDITIONS,
  Filter,
  FilterForm,
  FilterTypeValue,
  NUMBER_CONDITIONS,
} from 'src/app/interfaces/groups-filters/groups-filters.interface';
import { nonEmptyArrayValidator } from './validation.utils';

@Injectable({
  providedIn: 'root',
})
export class FilterService {
  constructor(private fb: FormBuilder) {}

  /**
   * Creates a new filter form group with default validators
   */
  createFilterGroup(filtersLength: number): FormGroup<FilterForm> {
    return this.fb.group<FilterForm>({
      type: this.fb.control<FilterTypeValue | null>(null, { validators: Validators.required }),
      condition: this.fb.control<ARRAY_CONDITIONS | null>(null, {
        validators: Validators.required,
      }),
      values: this.fb.control<string[] | string | null>(null, {
        validators: [Validators.required],
      }),
      priority: this.fb.control<number | null>(filtersLength + 1),
    });
  }

  /**
   * Sets up subscriptions for filter group changes and validation
   */
  createFilterGroupSubscriptions(
    filterGroup: FormGroup<FilterForm>,
    onValidFiltersChange: () => void,
  ): void {
    // Handle condition changes for dynamic validation
    filterGroup
      .get('condition')!
      .valueChanges.subscribe((condition: ARRAY_CONDITIONS | NUMBER_CONDITIONS | null) => {
        if (
          condition === ARRAY_CONDITIONS.CONTAINS_ALL_OF ||
          condition === ARRAY_CONDITIONS.NOT_CONTAINS_ALL_OF
        ) {
          filterGroup.get('values')!.setValidators([Validators.required, nonEmptyArrayValidator]);
        } else {
          filterGroup.get('values')!.setValidators([Validators.required]);
        }
        filterGroup.get('values')!.updateValueAndValidity();
      });

    // Handle form changes with debouncing
    filterGroup.valueChanges.pipe(debounceTime(1500), distinctUntilChanged()).subscribe(() => {
      if (filterGroup.valid) {
        onValidFiltersChange();
      }
    });
  }

  /**
   * Processes filter form groups and returns valid filters
   */
  getValidFilters(filters: FormArray<FormGroup<FilterForm>>): Filter[] {
    return filters.controls
      .filter((ctrl) => ctrl.valid)
      .map((ctrl) => ctrl.getRawValue())
      .map((filter) => {
        if (filter.values && !Array.isArray(filter.values)) {
          filter.values = [filter.values];
        }
        return filter as Filter;
      });
  }

  /**
   * Removes a filter at the specified index
   */
  removeFilter(filters: FormArray<FormGroup<FilterForm>>, index: number): void {
    filters.removeAt(index);
  }

  /**
   * Adds a new filter to the filters array
   */
  addFilter(filters: FormArray<FormGroup<FilterForm>>, onValidFiltersChange: () => void): void {
    const filterGroup = this.createFilterGroup(filters.length);
    this.createFilterGroupSubscriptions(filterGroup, onValidFiltersChange);
    filters.push(filterGroup);
  }

  /**
   * Clears all filters
   */
  clearFilters(filters: FormArray<FormGroup<FilterForm>>): void {
    filters.clear();
  }
}
